

import { Assessment, Category, Question, Answer, AnswerType } from '@/types/assessment';
import { createServiceClient } from '@/services/migration/serviceClient';

// Map database assessment to application model
export const mapDbAssessmentToModel = async (dbAssessment: any): Promise<Assessment> => {
  // Use service client to bypass RLS policies
  const serviceClient = createServiceClient();
  
  // Fetch categories for this assessment
  const { data: categoriesData, error: categoriesError } = await serviceClient
    .from('categories')
    .select('*')
    .eq('assessment_id', dbAssessment.id)
    .order('order_index');
    
  if (categoriesError) {
    console.error('Error fetching categories:', categoriesError);
    throw categoriesError;
  }
  
  const categories: Category[] = [];
  
  for (const categoryData of categoriesData || []) {
    // Fetch questions for this category
    const { data: questionsData, error: questionsError } = await serviceClient
      .from('questions')
      .select('*')
      .eq('category_id', categoryData.id)
      .order('order_index');
      
    if (questionsError) {
      console.error('Error fetching questions:', questionsError);
      throw questionsError;
    }
    
    const questions: Question[] = [];
    
    for (const questionData of questionsData || []) {
      // Fetch answers for this question
      const { data: answersData, error: answersError } = await serviceClient
        .from('answers')
        .select('*')
        .eq('question_id', questionData.id);
        
      if (answersError) {
        console.error('Error fetching answers:', answersError);
        throw answersError;
      }
      
      const answers: Answer[] = (answersData || []).map(answerData => ({
        id: answerData.id,
        text: answerData.text,
        value: answerData.value,
        recommendedActions: answerData.recommended_actions && typeof answerData.recommended_actions === 'string' ? 
          JSON.parse(answerData.recommended_actions) : 
          (Array.isArray(answerData.recommended_actions) ? answerData.recommended_actions : [])
      }));
      
      questions.push({
        id: questionData.id,
        text: questionData.text,
        explanation: questionData.explanation,
        answerType: (questionData.answer_type as AnswerType) || 'single',
        answers,
        required: questionData.required,
        premium: questionData.premium,
        disabled: questionData.disabled || false
      });
    }
    
    categories.push({
      id: categoryData.id,
      name: categoryData.name,
      description: categoryData.description,
      questions,
      order: categoryData.order_index
    });
  }
  
  // Fetch version history for this assessment
  const { data: versionHistoryData } = await serviceClient
    .from('version_history')
    .select('*')
    .eq('assessment_id', dbAssessment.id)
    .order('version_number', { ascending: false });
  
  const versionHistory = (versionHistoryData || []).map(versionData => ({
    versionNumber: versionData.version_number,
    timestamp: versionData.created_at,
    categories: typeof versionData.categories_snapshot === 'string' ? 
      JSON.parse(versionData.categories_snapshot) : 
      (versionData.categories_snapshot as unknown as Category[])
  }));
  
  return {
    id: dbAssessment.id,
    title: dbAssessment.title,
    description: dbAssessment.description,
    categories,
    version: dbAssessment.version,
    createdAt: dbAssessment.created_at,
    updatedAt: dbAssessment.updated_at,
    isActive: dbAssessment.is_active,
    isMaturityModel: dbAssessment.is_maturity_model,
    slug: dbAssessment.slug,
    notifyEmails: dbAssessment.notify_emails,
    calendlyLink: dbAssessment.calendly_link,
    assessmentType: dbAssessment.assessment_type,
    status: dbAssessment.status,
    versionHistory
  };
};

